import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ChevronLeft, SkipForward, Undo2, ExternalLink } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import PageLoader from '../components/PageLoader';
import { READINESS, READINESS_KEYS, setReadiness, formatDay, dueForReview } from '../utils/readiness';

const API_URL = process.env.REACT_APP_API_URL || '';
// Where a review got to, kept on this device so leaving and coming back resumes it.
// What's been reviewed lives on the server (readiness_reviewed_at).
const PROGRESS_KEY = 'gigrequest:songReview';

const loadProgress = () => {
  try { return JSON.parse(localStorage.getItem(PROGRESS_KEY)) || null; } catch { return null; }
};
const saveProgress = (p) => {
  try { p ? localStorage.setItem(PROGRESS_KEY, JSON.stringify(p)) : localStorage.removeItem(PROGRESS_KEY); } catch {}
};

const SCOPES = [
  ['all', 'All active songs'],
  ['unreviewed', 'Not reviewed yet'],
  ['due', 'Not reviewed for 6 months'],
];

const secondsToMMSS = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '');

// Song review mode (request list curation spec §3.1a): one song at a time with big
// Gig-ready / Rusty / Not ready buttons, for a band's first audit of its library and
// occasional re-audits. Each tap saves straight away and moves on.
export default function SongReview() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [songs, setSongs] = useState(null);
  const [scope, setScope] = useState(params.get('scope') || 'all');
  const [order, setOrder] = useState('oldest');
  const [progress, setProgress] = useState(loadProgress);   // { queue, index, label }
  const [history, setHistory] = useState([]);                // [{ id, prev, index }] for Undo
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/songs`)
      .then(res => setSongs(Array.isArray(res.data) ? res.data : []))
      .catch(() => { toast.error('Reviewing songs needs signal'); setSongs([]); });
  }, []);

  const byId = useMemo(() => new Map((songs || []).map(s => [s.id, s])), [songs]);
  const genres = useMemo(() => [...new Set((songs || []).filter(s => s.is_active).map(s => s.genre).filter(Boolean))].sort(), [songs]);

  const pick = useCallback(() => {
    const active = (songs || []).filter(s => s.is_active);
    let list = active;
    if (scope === 'unreviewed') list = active.filter(s => !s.readiness_reviewed_at);
    else if (scope === 'due') list = active.filter(s => dueForReview(s));
    else if (scope.startsWith('readiness:')) list = active.filter(s => s.readiness === scope.slice(10));
    else if (scope.startsWith('genre:')) list = active.filter(s => s.genre === scope.slice(6));
    const sorted = [...list].sort(order === 'az'
      ? (a, b) => a.title.localeCompare(b.title)
      // Oldest first; never-played songs first of all, as they're the likeliest to need a look
      : (a, b) => String(a.last_played || '').localeCompare(String(b.last_played || '')) || a.title.localeCompare(b.title));
    return sorted.map(s => s.id);
  }, [songs, scope, order]);

  const pickedCount = useMemo(() => pick().length, [pick]);

  const update = (next) => { setProgress(next); saveProgress(next); };

  const start = () => {
    const queue = pick();
    if (!queue.length) { toast('No songs match'); return; }
    const label = SCOPES.find(([k]) => k === scope)?.[1]
      || (scope.startsWith('readiness:') ? `${READINESS[scope.slice(10)].label} songs` : `${scope.slice(6)} songs`);
    setHistory([]);
    update({ queue, index: 0, label });
  };

  // Skip songs deleted or hidden since the queue was made
  const queue = useMemo(() => (progress?.queue || []).filter(id => byId.get(id)?.is_active), [progress, byId]);
  const index = Math.min(progress?.index ?? 0, queue.length);
  const song = queue[index] !== undefined ? byId.get(queue[index]) : null;
  const done = progress && index >= queue.length;

  const choose = useCallback(async (readiness) => {
    if (!song || saving) return;
    setSaving(true);
    try {
      await setReadiness([song.id], readiness);
      const reviewedAt = new Date().toISOString();
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, readiness, readiness_reviewed_at: reviewedAt } : s));
      setHistory(h => [...h, { id: song.id, prev: song.readiness, index }]);
      update({ ...progress, queue, index: index + 1 });
    } catch {
      toast.error("Couldn't save: check your signal");
    } finally {
      setSaving(false);
    }
  }, [song, saving, progress, queue, index]);  // eslint-disable-line react-hooks/exhaustive-deps

  const undo = async () => {
    const last = history[history.length - 1];
    if (!last || saving) return;
    setSaving(true);
    try {
      await setReadiness([last.id], last.prev);
      setSongs(prev => prev.map(s => s.id === last.id ? { ...s, readiness: last.prev } : s));
      setHistory(h => h.slice(0, -1));
      update({ ...progress, queue, index: last.index });
    } catch {
      toast.error("Couldn't undo: check your signal");
    } finally {
      setSaving(false);
    }
  };

  const move = (delta) => update({ ...progress, queue, index: Math.max(0, Math.min(queue.length, index + delta)) });
  const finish = () => { update(null); setHistory([]); };

  // Keyboard on a laptop: 1 / 2 / 3 to choose, S to skip
  useEffect(() => {
    if (!song) return;
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      const k = { '1': 'ready', '2': 'rusty', '3': 'not_ready' }[e.key];
      if (k) choose(k);
      else if (e.key.toLowerCase() === 's') move(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });  // eslint-disable-line react-hooks/exhaustive-deps

  if (!songs) return <PageLoader />;

  const page = { height: '100%', overflowY: 'auto', background: theme.background, color: theme.text };
  const inner = { maxWidth: 520, margin: '0 auto', padding: '1rem' };
  const chip = (activeChip) => ({
    padding: '0.45rem 0.8rem', borderRadius: 20, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
    background: activeChip ? theme.primary : theme.surfaceAlt, color: activeChip ? theme.onPrimary : theme.textSecondary,
    border: activeChip ? 'none' : `1px solid ${theme.border}`, fontWeight: activeChip ? 700 : 400,
  });
  const plainBtn = {
    background: 'transparent', border: `1px solid ${theme.border}`, color: theme.text, borderRadius: 10,
    padding: '0.7rem 1rem', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
  };

  // ── Choosing what to review ──────────────────────────────
  if (!progress) {
    const active = songs.filter(s => s.is_active);
    const reviewed = active.filter(s => s.readiness_reviewed_at).length;
    return (
      <div style={page}>
        <div style={inner}>
          <button onClick={() => navigate('/dashboard/songs')} style={{ ...plainBtn, border: 'none', padding: '0.3rem 0', marginBottom: '0.5rem' }}>
            <ChevronLeft size={16} /> Songs
          </button>
          <h2 style={{ margin: '0 0 0.3rem' }}>Review songs</h2>
          <p style={{ margin: '0 0 1.25rem', color: theme.textSecondary, fontSize: '0.92rem', lineHeight: 1.5 }}>
            Go through your songs one at a time and mark each one. Only <b>gig-ready</b> songs are offered to the audience,
            unless a gig is set to also offer rusty ones. Not-ready songs still work in set lists and the teleprompter.
            <br /><span style={{ color: theme.textMuted }}>{reviewed} of {active.length} active songs reviewed so far.</span>
          </p>

          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Which songs</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
            {SCOPES.map(([k, label]) => <button key={k} onClick={() => setScope(k)} style={chip(scope === k)}>{label}</button>)}
            {READINESS_KEYS.map(k => (
              <button key={k} onClick={() => setScope(`readiness:${k}`)} style={chip(scope === `readiness:${k}`)}>
                {READINESS[k].emoji} {READINESS[k].label}
              </button>
            ))}
          </div>
          {genres.length > 0 && (
            <select value={scope.startsWith('genre:') ? scope : ''} onChange={e => e.target.value && setScope(e.target.value)}
              style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, marginBottom: '1.1rem', fontSize: '0.9rem' }}>
              <option value="">…or one genre</option>
              {genres.map(g => <option key={g} value={`genre:${g}`}>{g}</option>)}
            </select>
          )}

          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Order</div>
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.5rem' }}>
            <button onClick={() => setOrder('oldest')} style={chip(order === 'oldest')}>Longest since played</button>
            <button onClick={() => setOrder('az')} style={chip(order === 'az')}>A to Z</button>
          </div>

          <button onClick={start} disabled={!pickedCount}
            style={{ width: '100%', padding: '0.95rem', borderRadius: 12, border: 'none', background: theme.primary, color: theme.onPrimary,
              fontSize: '1rem', fontWeight: 700, cursor: pickedCount ? 'pointer' : 'default', opacity: pickedCount ? 1 : 0.5, fontFamily: 'inherit' }}>
            {pickedCount ? `Start: ${pickedCount} song${pickedCount === 1 ? '' : 's'}` : 'No songs match'}
          </button>
        </div>
      </div>
    );
  }

  // ── Finished ─────────────────────────────────────────────
  if (done) {
    const counts = READINESS_KEYS.map(k => [k, queue.filter(id => byId.get(id)?.readiness === k).length]);
    return (
      <div style={page}>
        <div style={{ ...inner, textAlign: 'center', paddingTop: '2.5rem' }}>
          <div style={{ fontSize: '2.5rem' }}>🎉</div>
          <h2 style={{ margin: '0.5rem 0' }}>All done</h2>
          <p style={{ color: theme.textSecondary, margin: '0 0 1.25rem' }}>{progress.label}: {queue.length} song{queue.length === 1 ? '' : 's'}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '1.75rem' }}>
            {counts.map(([k, n]) => (
              <div key={k}><div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{n}</div><div style={{ fontSize: '0.85rem', color: theme.textMuted }}>{READINESS[k].emoji} {READINESS[k].label}</div></div>
            ))}
          </div>
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {history.length > 0 && <button onClick={undo} style={plainBtn}><Undo2 size={15} /> Undo last</button>}
            <button onClick={() => { finish(); navigate('/dashboard/songs'); }} style={{ ...plainBtn, background: theme.primary, color: theme.onPrimary, border: 'none', fontWeight: 700 }}>Back to songs</button>
            <button onClick={finish} style={plainBtn}>Review more</button>
          </div>
        </div>
      </div>
    );
  }

  // ── One song ─────────────────────────────────────────────
  const query = encodeURIComponent(`${song.title} ${song.artist_original}`);
  const current = READINESS[song.readiness] || READINESS.ready;
  return (
    <div style={page}>
      <div style={inner}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
          <button onClick={finish} style={{ ...plainBtn, border: 'none', padding: '0.3rem 0' }}><ChevronLeft size={16} /> Stop</button>
          <span style={{ fontSize: '0.85rem', color: theme.textMuted }}>{index + 1} of {queue.length}</span>
        </div>
        <div style={{ height: 4, background: theme.surfaceAlt, borderRadius: 2, overflow: 'hidden', marginBottom: '1rem' }}>
          <div style={{ height: '100%', width: `${(index / queue.length) * 100}%`, background: theme.primary, transition: 'width 0.2s' }} />
        </div>

        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 16, padding: '1.25rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, lineHeight: 1.2 }}>{song.title}</div>
          <div style={{ color: theme.textSecondary, marginTop: '0.25rem' }}>{song.artist_original}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.9rem', marginTop: '0.75rem', fontSize: '0.85rem', color: theme.textMuted }}>
            {song.genre && <span>{song.genre}</span>}
            {song.duration_seconds && <span>{secondsToMMSS(song.duration_seconds)}</span>}
            <span>{song.last_played ? `Last played ${formatDay(song.last_played)}` : 'Not played at a gig yet'}</span>
            <span>Now: {current.emoji} {current.label}{song.readiness_reviewed_at ? '' : ' (not reviewed)'}</span>
          </div>
          {song.notes && <div style={{ marginTop: '0.75rem', fontSize: '0.88rem', fontStyle: 'italic', color: theme.textSecondary }}>{song.notes}</div>}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.9rem', fontSize: '0.85rem' }}>
            <a href={`https://open.spotify.com/search/${query}`} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, display: 'flex', alignItems: 'center', gap: 4 }}>
              Spotify <ExternalLink size={12} />
            </a>
            <a href={`https://www.youtube.com/results?search_query=${query}`} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, display: 'flex', alignItems: 'center', gap: 4 }}>
              YouTube <ExternalLink size={12} />
            </a>
          </div>
        </div>

        <div style={{ display: 'grid', gap: '0.6rem', marginBottom: '0.9rem' }}>
          {READINESS_KEYS.map(k => (
            <button key={k} onClick={() => choose(k)} disabled={saving}
              style={{ padding: '1.05rem', borderRadius: 14, fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                background: READINESS[k].color, color: '#fff', border: song.readiness === k ? '3px solid ' + theme.text : '3px solid transparent',
                opacity: saving ? 0.6 : 1 }}>
              {READINESS[k].emoji} {READINESS[k].label}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
          <button onClick={() => move(-1)} disabled={index === 0} style={{ ...plainBtn, opacity: index === 0 ? 0.4 : 1 }}><ChevronLeft size={15} /> Back</button>
          <button onClick={undo} disabled={!history.length} style={{ ...plainBtn, opacity: history.length ? 1 : 0.4 }}><Undo2 size={15} /> Undo</button>
          <button onClick={() => move(1)} style={plainBtn}>Skip <SkipForward size={15} /></button>
        </div>
      </div>
    </div>
  );
}
